import type { UpdateTrailerInput } from '@chukua/common';
import { Alert, Button, Form, Input, InputNumber, Select, Switch } from 'antd';
import { useEffect } from 'react';
import type { TrailerView } from '../trailers.api';
import { useTrailerTypeOptions } from '../hooks/use-trailer-type-options';

interface TrailerDetailsFormProps {
  trailer: TrailerView;
  canManage: boolean;
  error: string | null;
  saving: boolean;
  onSave: (input: UpdateTrailerInput) => Promise<boolean>;
}

function toFormValues(trailer: TrailerView): UpdateTrailerInput {
  return {
    registration: trailer.registration,
    typeCode: trailer.typeCode,
    capacityTonnes: trailer.capacityTonnes,
    axleCount: trailer.axleCount,
    isSkeletal: trailer.isSkeletal,
    isFlatRack: trailer.isFlatRack,
    isReefer: trailer.isReefer,
  };
}

/** The trailer's fields, editable inline for `truck.manage` holders — read-only for everyone else with `fleet.view` (CLAUDE.md: nav-hiding and disabled controls are UX only, the server remains the authority). */
export function TrailerDetailsForm({ trailer, canManage, error, saving, onSave }: TrailerDetailsFormProps) {
  const [form] = Form.useForm<UpdateTrailerInput>();
  const typeOptions = useTrailerTypeOptions();
  const options = typeOptions.some((type) => type.code === trailer.typeCode)
    ? typeOptions
    : [...typeOptions, { id: trailer.typeCode, code: trailer.typeCode, name: trailer.typeCode, isActive: false }];

  useEffect(() => {
    form.setFieldsValue(toFormValues(trailer));
  }, [trailer, form]);

  async function submit(values: UpdateTrailerInput) {
    await onSave(values);
  }

  return (
    <Form<UpdateTrailerInput>
      form={form}
      layout="vertical"
      requiredMark={false}
      onFinish={submit}
      disabled={!canManage || saving}
      initialValues={toFormValues(trailer)}
    >
      {error && <Alert className="mb-5" type="error" title={error} showIcon />}
      <Form.Item name="registration" label="Registration" rules={[{ required: true, message: 'Enter a registration' }]}>
        <Input className="font-mono" />
      </Form.Item>
      <Form.Item name="typeCode" label="Trailer type" rules={[{ required: true, message: 'Select a trailer type' }]}>
        <Select options={options.map((type) => ({ value: type.code, label: type.name }))} showSearch optionFilterProp="label" />
      </Form.Item>
      <div className="grid grid-cols-2 gap-3">
        <Form.Item name="capacityTonnes" label="Capacity (tonnes)" rules={[{ required: true, message: 'Enter a capacity' }]}>
          <InputNumber className="w-full" min={0} />
        </Form.Item>
        <Form.Item name="axleCount" label="Axle count" rules={[{ required: true, message: 'Enter an axle count' }]}>
          <InputNumber className="w-full" min={1} precision={0} />
        </Form.Item>
      </div>
      <Form.Item name="isSkeletal" label="Skeletal" valuePropName="checked">
        <Switch />
      </Form.Item>
      <Form.Item name="isFlatRack" label="Flat rack" valuePropName="checked">
        <Switch />
      </Form.Item>
      <Form.Item name="isReefer" label="Reefer" valuePropName="checked">
        <Switch />
      </Form.Item>
      {canManage && (
        <Form.Item className="mb-0">
          <Button type="primary" htmlType="submit" loading={saving}>
            Save changes
          </Button>
        </Form.Item>
      )}
    </Form>
  );
}
